import React, { useEffect } from "react"
import { Link } from "react-router-dom"
import { NoResultMessage } from "./NoResultMessage"
import {
  providerFinderRootPath,
  ProviderFinderParam,
  getServiceFormatSearched
} from "../../utils/providerFinderSearchUrl"
import { getHeap } from "../../utils/heap"
import { defineMessage, FormattedMessage } from "react-intl"
import { Box, Button, Progress, Typography } from "@carrotfertility/carotene-core"
import { useEnhancedEmptyState } from "#/pages/provider-finder/hooks/useEnhancedEmptyState"
import { ProviderEligibilityMessage } from "#/pages/provider-finder/components/results/ProviderEligibilityMessage"
import { useProviderTypeSearched } from "./useProviderTypeSearched"
import { DoulaNoResultDisplay } from "./DoulaNoResultDisplay"
import { PelvicHealthNoResultDisplay } from "./PelvicHealthNoResultDisplay"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { ProviderType } from "#/pages/provider-finder/utils/parentIdMapping"
import { useHistory } from "react-router"
import { NoResultLayout } from "./NoResultLayout"
import { parseBookingEntryPayment } from "@internal/constants"

export function NoResultDisplay({ isCountryCodeMatching }: { isCountryCodeMatching: boolean }): JSX.Element {
  const { providerTypeSearched } = useProviderTypeSearched()
  const { mutate: trackMemberAction } = useMemberActionTracking()
  const history = useHistory()
  const params = new URLSearchParams(history.location.search)
  const payment = parseBookingEntryPayment(params.get("payment"))
  const searchLocation = params.get(ProviderFinderParam.Search)
  const keyword = params.get(ProviderFinderParam.Keyword)
  const serviceFormat = getServiceFormatSearched(params)
  useEffect(
    function trackProviderSearchNoResultsMessageRendered() {
      getHeap().track("Provider Search No Results Message Rendered", {
        "Location Is Outside Country": !isCountryCodeMatching ? "true" : "false"
      })
    },
    [isCountryCodeMatching]
  )

  useEffect(() => {
    trackMemberAction({
      memberActionTaken: MemberActions.PROVIDER_FINDER_EMPTY_STATE_VIEWED,
      metaData: {
        providerType: providerTypeSearched,
        keyword: keyword ?? undefined,
        searchLocation: searchLocation ?? undefined
      }
    })
  }, [providerTypeSearched, keyword, searchLocation, trackMemberAction])

  const enhancedEmptyState = useEnhancedEmptyState()

  if (providerTypeSearched === ProviderType.Doulas) {
    return <DoulaNoResultDisplay isCountryCodeMatching={isCountryCodeMatching} />
  }

  if (providerTypeSearched === ProviderType.PelvicFloorTherapy) {
    return <PelvicHealthNoResultDisplay isCountryCodeMatching={isCountryCodeMatching} />
  }

  const headerMessage = enhancedEmptyState.isEnhancedEmptyState
    ? defineMessage({ defaultMessage: "No results in Provider finder, but..." })
    : defineMessage({ defaultMessage: "No results found" })

  const footerMessage = enhancedEmptyState.isEnhancedEmptyState
    ? defineMessage({ defaultMessage: "Search again" })
    : defineMessage({ defaultMessage: "Reset location" })

  if (enhancedEmptyState.isLoadingContent) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Progress />
      </Box>
    )
  }

  return (
    <NoResultLayout
      header={
        <Typography variant="h4" color="secondary">
          <FormattedMessage {...headerMessage} />
        </Typography>
      }
      body={
        enhancedEmptyState.isEnhancedEmptyState ? (
          <ProviderEligibilityMessage
            displayName={enhancedEmptyState.emptyStateProviderTypeDisplayName}
            dialogContent={enhancedEmptyState.emptyStateContent}
          />
        ) : (
          <NoResultMessage isCountryCodeMatching={isCountryCodeMatching} serviceFormat={serviceFormat} />
        )
      }
      actions={
        <Link
          to={{
            pathname: providerFinderRootPath,
            search: payment ? new URLSearchParams({ payment }).toString() : ""
          }}
        >
          <Button variant="outlined" color="secondary" size="medium">
            <FormattedMessage {...footerMessage} />
          </Button>
        </Link>
      }
    />
  )
}
